<template>
  <m-pageframe title="{organization-label}: Order Confirmation Forms">
    <v-container class="mb-10">
      <m-beta-alert v-if="!is_missing_required_integration" code="beta-order-placed-request-order-confirmation" />

      <alert-integration-required
        ref="integration-alert"
        feature_identifier="order-confirmation"
        feature_name="Order Confirmation"
        @integration-status-changed="onIntegrationStatusChanged"
      />

      <card-grid-with-controls :show_loading_state="loading">
        <template #header>
          <h1 class="text-h4 font-weight-bold mb-1">Order Confirmation Forms</h1>
          <p class="text-body-2 text--secondary mb-0">
            Create and customize order confirmation forms and collect customer consent with an automated message
          </p>
        </template>

        <template #header-actions>
          <v-btn
            color="primary"
            :disabled="is_missing_required_integration || loading"
            @click="onClickAddOrderConfirmation"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            Create Order Confirmation Form
          </v-btn>

          <v-menu
            :disabled="loading"
            bottom
            offset-y
            left
          >
            <template #activator="{ on }">
              <v-btn
                icon
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list
              class="pt-0"
              dense
            >
              <v-subheader>ANALYTICS & ENTRIES</v-subheader>

              <v-list-item @click="viewOrderConfirmationSubmissions">
                <v-list-item-icon><v-icon>mdi-clipboard-list</v-icon></v-list-item-icon>
                <v-list-item-title>All Order Confirmation Entries</v-list-item-title>
              </v-list-item>

              <v-list-item @click="viewOrderConfirmationAnalytics">
                <v-list-item-icon><v-icon>mdi-chart-line</v-icon></v-list-item-icon>
                <v-list-item-title>All Order Confirmation Analytics</v-list-item-title>
              </v-list-item>
              
              <template v-if="order_conf_auto_message_label && order_conf_auto_message_link">
                <v-divider />

                <v-subheader>AUTOMATED MESSAGES</v-subheader>
  
                <v-list-item @click="viewAllAutomatedMessages">
                  <v-list-item-icon><v-icon>mdi-forum</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ order_conf_auto_message_label }}</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </template>

        <template #filter>
          <div class="d-flex align-center gap-4">
            <v-btn-toggle
              v-model="filter_status"
              dense
              mandatory
            >
              <v-btn value="all">All ({{ num_all }})</v-btn>
              <v-btn value="published">Published ({{ num_published }})</v-btn>
              <v-btn value="draft">Drafts ({{ num_drafts }})</v-btn>
            </v-btn-toggle>

            <v-checkbox
              v-model="show_archived"
              label="Show Archived"
              :disabled="loading"
              class="mt-0 pt-0"
              hide-details
            />
          </div>
        </template>

        <template #search>
          <v-text-field
            label="Search"
            prepend-inner-icon="mdi-magnify"
            autocomplete="off"
            :disabled="loading"
            v-model="full_text"
            dense
            hide-details
            clearable
            outlined
          />
        </template>

        <template #grid>
          <v-col
            v-for="item in results"
            :key="item.order_confirmation_settings_id"
            cols="12"
            md="6"
            lg="4"
          >
            <card-with-action-header
              :header_text="item.internal_label"
              :menu_items="getMenuItems(item)"
              :menu_aria_label="`Actions for ${item.internal_label} form`"
              :aria_label="`Edit form: ${item.internal_label}`"
              :is_archived="item.is_archived"
              :is_draft="isItemDraft(item)"
              interactive
              @click="onClickEdit(item)"
              @menu-action="onMenuAction"
            >
              <template #subheader>
                <v-chip
                  v-if="isLinkedToAutoMessage(item)"
                  small
                  class="linked-auto-message-chip font-weight-medium"
                  color="success"
                  text-color="white"
                >
                  <v-icon
                    left
                    small
                    class="mr-1"
                  >mdi-check-circle</v-icon>
                  Used by Automated Message
                </v-chip>
              </template>

              <div class="pa-4">
                <div class="text--secondary mb-3 d-flex justify-space-between align-center">
                  <div class="d-flex align-center gap-1">
                    <v-icon
                      small
                      color="primary"
                      class="mr-1"
                    >mdi-clipboard-list-outline</v-icon>
                    Entries:
                  </div>
                  <strong class="ml-1">{{ item.total_submissions || 0 }}</strong>
                </div>

                <div class="text--secondary d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      :color="item.is_published ? 'success' : 'primary'"
                      class="mr-2"
                    >
                      {{ item.is_published ? 'mdi-check-circle' : 'mdi-pencil' }}
                    </v-icon>
                    Published:
                  </div>
                  <strong class="ml-1">{{ item.is_published ? 'Yes' : 'No' }}</strong>
                </div>

                <div
                  v-if="item.is_published && item.publish_time"
                  class="text--secondary d-flex justify-space-between align-center mt-3"
                >
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      color="primary"
                      class="mr-2"
                    >mdi-clock-outline</v-icon>
                    Last Published:
                  </div>
                  <strong class="ml-1">{{ $rc.pretty(item.publish_time, 'date_and_time') }}</strong>
                </div>

                <div
                  v-if="item.is_published && item.publish_by_employee_label"
                  class="text--secondary d-flex justify-space-between align-center mt-3"
                >
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      color="primary"
                      class="mr-2"
                    >mdi-account</v-icon>
                    Last Published By:
                  </div>
                  <strong class="ml-1">{{ item.publish_by_employee_label }}</strong>
                </div>


              </div>
            </card-with-action-header>
          </v-col>
        </template>

        <template #additional-content>
          <qr-code-dialog ref="qrCodeDialog" />

          <rc-info-dialog
            v-model="show_explainer_dialog"
            title="Welcome to Order Confirmation Forms!"
            :persistent="false"
            icon=""
          >
            <template #default>
              <div class="text-body-1 mb-4">
                <strong>
                  To get started, you'll need to complete the following steps:
                </strong>
              </div>

              <order-confirmation-explainer-steps />
            </template>

            <template #actions>
              <v-btn
                text
                @click="show_explainer_dialog = false"
              >
                Stay on this page
              </v-btn>

              <v-spacer />

              <v-btn
                color="primary"
                @click="onClickCreateFirst"
              >
                Create My First Form
              </v-btn>
            </template>
          </rc-info-dialog>
        </template>
      </card-grid-with-controls>
    </v-container>
  </m-pageframe>
</template>

<script>
import CardGridWithControls from '@/components/layouts/card-grid-with-controls.vue';
import CardWithActionHeader from '@/components/shared/cards/card-with-action-header.vue';
import AlertIntegrationRequired from '@/components/global/alert-integration-required.vue';
import OrderConfirmationExplainerSteps from '@/components/order-confirmation/order-confirmation-explainer-steps.vue';
import _ from 'lodash';

export default {
  name: 'SearchFormsOrderConfirmation',

  components: {
    CardGridWithControls,
    CardWithActionHeader,
    AlertIntegrationRequired,
    OrderConfirmationExplainerSteps,
    QrCodeDialog: () => import('@/components/input/qr-code-dialog.vue'),
  },

  data() {
    return {
      show_explainer_dialog: false,
      all_settings: [],
      order_conf_auto_message_link: null,
      order_conf_auto_message_label: '',
      message_id_linked_to_auto_message: null,
      comp_best_send_mode: null,
      full_text: null,
      filter_status: 'all', // 'published', 'draft', 'all'
      show_archived: false,
      loading: false,
      is_missing_required_integration: false,
    };
  },

  mounted() {
    this.doUpdate();
    this.getOrderConfAutomatedMessage()
  },

  methods: {
    update: _.debounce(function () {
      this.doUpdate();
    }, 500),

    onIntegrationStatusChanged(has_integration) {
      this.is_missing_required_integration = !has_integration;
    },

    doUpdate() {
      this.loading = true;

      const params = {
        organization_id: this.$route.params.organization_id,
        full_text: this.full_text,
      };

      // Don't add filters when showing 'all'
      if (this.filter_status !== 'all') {
        if (this.filter_status === 'archived') {
          params.is_archived = true;
        } else if (this.filter_status === 'published') {
          params.is_archived = false;
          params.is_published = true;
        } else if (this.filter_status === 'draft') {
          params.is_archived = false;
          params.is_published = false;
        }
      }

      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/order-confirmation/settings/search',
        params: params,
        on_success: data => {
          // move archived forms to end
          this.all_settings = data.results?.sort((a, b) => {
            if (a.is_archived === b.is_archived) return 0;
            if (a.is_archived) return 1;
            return -1;
          });

          this.loading = false;

          if (this.all_settings.length === 0 && !this.is_missing_required_integration) {
            this.show_explainer_dialog = true;
          }
        },
        on_error: 'Unable to load order confirmation forms',
        on_finally: () => {
          this.loading = false;
        },
      });
    },

    getOrderConfAutomatedMessage() {
      this.$rc.rest({
          spinner: false,
          endpoint: 'PUT: /api/v1/auto-messages/org-profile',

          params : {
            organization_id: this.$route.params.organization_id,
          },

          on_success: (data) => {
            const order_conf_auto_message = data.templates.find(({ template_context }) => template_context === 'order-placed-request-order-confirmation');
            
            if (order_conf_auto_message) {
              this.order_conf_auto_message_link = `/app/auto-messages/${this.$route.params.organization_id}/order-placed-request-order-confirmation/00000000-0000-0000-0000-000000000000`;
              this.order_conf_auto_message_label = order_conf_auto_message.template_label;
              this.message_id_linked_to_auto_message = order_conf_auto_message.order_confirmation_settings_id || null;
              this.comp_best_send_mode = order_conf_auto_message.comp_best_send_mode || null;
            }
          },
          on_error: 'Unable to list automated messages',
      });
    },

    isItemDraft(item) {
      return !item.is_archived && !item.is_published;
    },

    isLinkedToAutoMessage(item) {
      if (!item || !this.message_id_linked_to_auto_message) {
        return false;
      }

      return this.message_id_linked_to_auto_message === item.order_confirmation_settings_id;
    },

    canViewInboxLink(item) {
      return !!(item && item.is_published && item.update_order_inbox_id);
    },

    inboxContactRequestsRoute(item) {
      if (!item) return null;
      return {
        name: 'search-inbox-contact-requests',
        params: {
          organization_id: this.$route.params.organization_id,
          inbox_id: item.update_order_inbox_id,
        },
      };
    },

    onClickEdit(item) {
      this.$router.push({
        name: 'order-confirmation-edit',
        params: {
          organization_id: this.$route.params.organization_id,
          order_confirmation_settings_id: item.order_confirmation_settings_id,
        },
      });
    },

    getMenuItems(item) {
      const menu_items = [
        {
          text: 'Edit',
          action: 'edit',
          icon: 'mdi-pencil',
          item,
        },

        {
          text: item.is_archived ? 'Unarchive' : 'Archive',
          action: 'toggle-archive',
          icon: item.is_archived ? 'mdi-package-up' : 'mdi-archive',
          item,
        },
      ];

      if (item.is_published && !item.is_archived) {
        menu_items.push(
          {
            text: 'Duplicate',
            action: 'duplicate',
            icon: 'mdi-content-copy',
            item,
          }
        );
        menu_items.push({ divider: true });
        menu_items.push({
          text: 'View Entries',
          action: 'view-submissions',
          icon: 'mdi-clipboard-list',
          item,
        });
        menu_items.push({
          text: 'View Analytics',
          action: 'view-analytics',
          icon: 'mdi-chart-line',
          item,
        });

        if (this.canViewInboxLink(item)) {
          menu_items.push({
            text: 'View Contact Requests',
            action: 'view-contact-requests',
            icon: 'mdi-inbox-arrow-down',
            item,
          });
        }
      }

      return menu_items;
    },

    onMenuAction(action, item) {
      const { item: settings } = item;

      switch (action) {
        case 'edit':
          this.onClickEdit(settings);
          break;
        case 'view-submissions':
          this.onClickViewSubmissions(settings);
          break;
        case 'view-analytics':
          this.onClickViewAnalytics(settings);
          break;
        case 'toggle-archive':
          this.onClickToggleArchive(settings);
          break;
        case 'duplicate':
          this.onClickDuplicate(settings);
          break;
        case 'view-contact-requests':
          this.onClickViewContactRequests(settings);
          break;
      }
    },
    onClickViewContactRequests(item) {
      const route = this.inboxContactRequestsRoute(item);
      if (route) {
        const routeData = this.$router.resolve(route);
        window.open(routeData.href, '_blank');
      }
    },
    onClickViewAnalytics(item) {
      this.$router.push({
        name: 'order-confirmation-analytics',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        query: {
          order_confirmation_settings_id: item.order_confirmation_settings_id,
        },
      });
    },

    onClickToggleArchive(item) {
      const action = item.is_archived ? 'Unarchive' : 'Archive';
      const is_archiving = !item.is_archived;
      const is_linked_to_auto_message = this.message_id_linked_to_auto_message === item.order_confirmation_settings_id;
      const message_label = this.order_conf_auto_message_label || 'Order Confirmation automated message';
      const auto_message_link = this.order_conf_auto_message_link
        || `/app/auto-messages/${this.$route.params.organization_id}/order-placed-request-order-confirmation/00000000-0000-0000-0000-000000000000`;

      if (is_archiving && is_linked_to_auto_message && this.comp_best_send_mode === 'auto') {
        this.$rc.alert(
          'Update Automated Message First',
          `The automated message "${message_label}" is configured to send automatically with this form. Update the automated message to not send automatically or disconnect this form before attempting to archive it.<br /><br /><a href="${auto_message_link}" target="_blank" rel="noopener">Open the automated message</a>`
        );
        return;
      }

      let body = `You are about to ${action.toLowerCase()} the form: "${item.internal_label}". `;
      body += is_archiving
        ? 'Archiving moves the form into the Archived state so it is hidden from customers and automations.'
        : 'Unarchiving moves the form back into the active workspace so it can be edited or published again.';

      if (is_archiving && is_linked_to_auto_message) {
        body += `<br /><br />Warning: This form is currently used by the order confirmation automated message. You can archive the form, but archiving will block any messages from being able to send.<br /><br /><a href="${auto_message_link}" target="_blank" rel="noopener">Open the automated message</a>`;
      }

      this.$rc.confirm({
        title: `${action} Form`,
        body,
        btn_text_ok: action,
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doToggleArchived(item),
      });
    },

    onClickDuplicate(item) {
      this.$rc.confirm({
        title: 'Duplicate Form',
        body: 'Are you sure you want to duplicate the form: ' + item.internal_label,
        btn_text_ok: 'Duplicate',
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doDuplicate(item),
      });
    },

    doDuplicate(item) {
      const settings_id = item.order_confirmation_settings_id;
      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/duplicate',
        params: { order_confirmation_settings_id: settings_id },
        on_success: () => {
          this.doUpdate();
        },
        on_error: 'Unable to duplicate form',
      });
    },

    doToggleArchived(item) {
      const settings_id = item.order_confirmation_settings_id;
      const archived = !item.is_archived;
      const endpoint = archived ? 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/archive' : 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/unarchive';
      this.$rc.rest({
        spinner: true,
        endpoint,
        params: { order_confirmation_settings_id: settings_id },
        on_success: () => {
          this.doUpdate();
        },
        on_error: (error) => {
          this.$rc.alert('Error', error.message || 'Unable to update form status');
        },
      });
    },

    onClickAddOrderConfirmation() {
      this.$router.push({
        name: 'order-confirmation-template-selection',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewOrderConfirmationAnalytics() {
      this.$router.push({
        name: 'order-confirmation-analytics',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewAllAnalytics() {
      this.$router.push({
        name: 'all-analytics',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewAllAutomatedMessages() {
      this.$router.push(
        this.order_conf_auto_message_link
      );
    },

    viewOrderConfirmationSubmissions() {
      this.$router.push({
        name: 'order-confirmation-submissions',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    onClickViewSubmissions(item) {
      this.$router.push({
        name: 'order-confirmation-submissions',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        query: {
          order_confirmation_settings_id: item.order_confirmation_settings_id,
        },
      });
    },

    onClickCreateFirst() {
      this.show_explainer_dialog = false;
      this.onClickAddOrderConfirmation();
    },
  },

  computed: {
    full_text_sanitized() {
      return (this.full_text || '').trim().toLowerCase();
    },

    results() {
      if (!this.all_settings) return [];

      // front-end filtering so we have access to the number of items under each filter
      return this.all_settings.filter(item => {
        const matches_text = item.internal_label.toLowerCase().includes(this.full_text_sanitized);

        if (!matches_text) return false;

        if (!this.show_archived && item.is_archived) {
          return false;
        }

        if (this.filter_status === 'all') {
          return true;
        } else if (this.filter_status === 'published') {
          return item.is_published;
        } else if (this.filter_status === 'draft') {
          return !item.is_published;
        }

        return true;
      });
    },

    num_drafts() {
      return this.all_settings.filter(item => {
        if (!this.show_archived && item.is_archived) return false;
        return !item.is_published;
      }).length;
    },

    num_published() {
      return this.all_settings.filter(item => {
        if (!this.show_archived && item.is_archived) return false;
        return item.is_published;
      }).length;
    },

    num_archived() {
      return this.all_settings.filter(item => item.is_archived).length;
    },

    num_all() {
      return this.all_settings.filter(item => {
        if (!this.show_archived && item.is_archived) return false;
        return true;
      }).length;
    },

    is_creating_very_first_form() {
      return this.all_settings.length === 0;
    },

    has_none_published_with_drafts() {
      return this.filter_status === 'published' && this.num_drafts > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 84rem;
}

.oc-card {
  border-radius: 12px;
  transition: box-shadow 150ms ease, transform 150ms ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px);
  }

  &.archived {
    opacity: 0.7;
    background-color: #f5f5f5;

    &:hover {
      opacity: 1;
    }
  }
}

.linked-auto-message-chip {
  letter-spacing: 0.02em;
}
</style>
